import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import KbEditor from "../../components/knowledgebase/KbEditor";
import KbViewer from "../../components/knowledgebase/KbViewer";
import KbArticleSettings from "../../components/knowledgebase/KbArticleSettings";
import { KnowledgebaseAPI } from "../../api/knowledgebase";
import { DepartmentsAPI, CategoriesAPI, SubcategoriesAPI } from "../../api/lookups";
import "../../components/knowledgebase/KbEditor.css";
const EMPTY = { title: "", body: { type: "doc", content: [{ type: "paragraph" }] }, audience_type: "end_user", department_id: "", category_id: "", subcategory_id: "", is_published: false };
export default function KbArticleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(EMPTY);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(true);
  useEffect(() => {
    let active = true;
    DepartmentsAPI.list().then((data) => { if (active) setDepartments(Array.isArray(data) ? data : data?.data || []); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    if (!id) { setArticle(EMPTY); setLoading(false); return () => { active = false; }; }
    setLoading(true);
    KnowledgebaseAPI.get(id).then((result) => { if (active) setArticle({ ...EMPTY, ...result.data }); })
      .catch((e) => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);
  useEffect(() => {
    let active = true;
    setCategories([]);
    if (article.department_id) CategoriesAPI.list(article.department_id).then((data) => { if (active) setCategories(Array.isArray(data) ? data : data?.data || []); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [article.department_id]);
  useEffect(() => {
    let active = true;
    setSubcategories([]);
    if (article.category_id) SubcategoriesAPI.list(article.category_id).then((data) => { if (active) setSubcategories(Array.isArray(data) ? data : data?.data || []); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [article.category_id]);
  const update = (next) => { setArticle(next); setSaved(""); };
  const save = async (publish) => {
    setBusy(true); setError(""); setSaved("");
    try {
      const payload = { ...article, is_published: publish, department_id: article.department_id || null, category_id: article.category_id || null, subcategory_id: article.subcategory_id || null };
      const response = id ? await KnowledgebaseAPI.update(id, payload) : await KnowledgebaseAPI.create(payload);
      setArticle({ ...EMPTY, ...response.data });
      setSaved(publish ? "Published successfully." : "Draft saved to Nightjar.");
      if (!id) navigate(`/knowledgebase/articles/${response.data.id}/edit`, { replace: true });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  if (loading) return <main className="kbv2-page"><div className="kbv2-state">Loading article…</div></main>;
  return <main className="kbv2-page kbv2-workspace">
    <div className="kbv2-workspace-bar">
      <div className="kbv2-crumbs"><Link to="/knowledgebase">← Knowledge Base</Link><span>/</span><span>{id ? "Edit article" : "New article"}</span></div>
      <div className="kbv2-workspace-actions">
        <span className={`kbv2-status ${article.is_published ? "is-live" : ""}`}>{article.is_published ? "● Published" : "● Draft"}</span>
        <button type="button" className="kbv2-btn kbv2-btn-quiet" onClick={() => setSettingsOpen(!settingsOpen)} aria-expanded={settingsOpen}>{settingsOpen ? "Hide settings" : "Article settings"}</button>
        <button type="button" className="kbv2-btn kbv2-btn-quiet" onClick={() => setPreview(!preview)}>{preview ? "Edit article" : "Preview"}</button>
        <button type="button" className="kbv2-btn" disabled={busy} onClick={() => save(false)}>{busy ? "Saving…" : "Save draft"}</button>
        <button type="button" className="kbv2-btn kbv2-btn-primary" disabled={busy} onClick={() => save(true)}>{busy ? "Saving…" : "Publish"}</button>
      </div>
    </div>
    {(error || saved) && <div className="kbv2-flashes">{error && <p className="kbv2-error" role="alert">{error}</p>}{saved && <p className="kbv2-saved" role="status">{saved}</p>}</div>}
    <div className={`kbv2-workspace-layout ${settingsOpen ? "has-settings" : ""}`}>
      <div className="kbv2-document-wrap">
        <div className="kbv2-document-meta"><span>KNOWLEDGE BASE</span><span> / </span><span>{preview ? "Preview" : "Editor"}</span></div>
        <article className="kbv2-document">
          {preview ? <><h1 className="kbv2-document-heading">{article.title || "Untitled article"}</h1><KbViewer value={article.body} /></> : <>
            <input className="kbv2-title" aria-label="Article title" placeholder="Untitled article" value={article.title} onChange={(e) => update({ ...article, title: e.target.value })} />
            <KbEditor value={article.body} onChange={(body) => update({ ...article, body })} />
          </>}
        </article>
        <div className="kbv2-document-foot">Changes are saved when you select Save draft or Publish.</div>
      </div>
      {settingsOpen && <div className="kbv2-settings-wrap"><KbArticleSettings article={article} onChange={update} departments={departments} categories={categories} subcategories={subcategories} /></div>}
    </div>
  </main>;
}
